import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { StuffInRewardBoxType } from '@sorare/core/src/components/rewards/RewardBoxDialog/types';
import { isType } from '@sorare/core/src/lib/gql';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';
import { lazy } from '@sorare/core/src/lib/retry';

import {
  ConversionCreditInRewardBox_ProbabilisticBundleSlotItemInterface,
  ConversionCreditInRewardBox_ProbabilisticBundleSlotItemInterface_ProbabilisticBundleSlotConversionCreditItem_,
} from './__generated__/index.graphql';

const ConversionCreditPreloader = lazy(
  async () => import('@sorare/trois/src/atoms/ConversionCredit/preloader'),
  'ConversionCreditPreloader'
);

const ConversionCreditInRewardBoxTrois = lazy(
  async () =>
    import(
      '@sorare/trois/src/components/RewardBox/ConversionCreditInRewardBox'
    ),
  'ConversionCreditInRewardBox'
);

const NoWrap = styled.div`
  white-space: nowrap;
`;

type Props = {
  item: ConversionCreditInRewardBox_ProbabilisticBundleSlotItemInterface;
  visible: boolean;
};

const ConversionCreditInRewardBoxCanHandle = (
  item: Props['item']
): item is ConversionCreditInRewardBox_ProbabilisticBundleSlotItemInterface_ProbabilisticBundleSlotConversionCreditItem_ => {
  return isType(item, 'ProbabilisticBundleSlotConversionCreditItem');
};

export const ConversionCreditInRewardBox: StuffInRewardBoxType<
  ConversionCreditInRewardBox_ProbabilisticBundleSlotItemInterface
> = ({ item, visible }: Props) => {
  if (!ConversionCreditInRewardBoxCanHandle(item)) {
    return null;
  }

  return (
    <ConversionCreditInRewardBoxTrois
      visible={visible}
      amount={(item.maxDiscount.usd ?? 100) / 100}
      description={
        <NoWrap>
          <AmountWithConversion monetaryAmount={item.maxDiscount} />

          <FormattedMessage
            id="ConversionCreditReward.marketCredit"
            defaultMessage="Market credit"
          />
        </NoWrap>
      }
    />
  );
};

ConversionCreditInRewardBox.fragments = {
  ProbabilisticBundleSlotItemInterface: gql`
    fragment ConversionCreditInRewardBox_ProbabilisticBundleSlotItemInterface on ProbabilisticBundleSlotItemInterface {
      id
      ... on ProbabilisticBundleSlotConversionCreditItem {
        id
        maxDiscount {
          ...MonetaryAmountFragment_monetaryAmount
        }
      }
    }
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<ConversionCreditInRewardBox_ProbabilisticBundleSlotItemInterface>,
};

const ConversionCreditInRewardBoxPreloader = () => {
  return <ConversionCreditPreloader />;
};

ConversionCreditInRewardBox.preloader = ConversionCreditInRewardBoxPreloader;

ConversionCreditInRewardBox.canHandle = ConversionCreditInRewardBoxCanHandle;

ConversionCreditInRewardBox.fallback = (item: Props['item']) => {
  if (!ConversionCreditInRewardBoxCanHandle(item)) {
    return null;
  }
  return (
    <div>
      <AmountWithConversion monetaryAmount={item.maxDiscount} />
      <FormattedMessage
        id="ConversionCreditReward.marketCredit"
        defaultMessage="Market credit"
      />
    </div>
  );
};

ConversionCreditInRewardBox.description = (item: Props['item']) => {
  if (!ConversionCreditInRewardBoxCanHandle(item)) {
    return null;
  }
  return 'Market credit';
};
